import type { FeatureDefinition } from '@growthbook/growthbook-react';

import type { PartnerData } from '@jerry-serverless/analyticslib/partner';
import type { InternalSource } from '@jerry-serverless/analyticslib/types';
import type { Income, InitialCar, FlowType } from '@jerry-serverless/types';
import type { SignupChallengeStep } from '@jerry-serverless/enums/user_challenge';

import type { FeatureData } from '../utils/enabled_features/types';
import type { LowestPrice } from '../apollo/graphql/lowest_price.query';
import type { SetPartnerAPIUserMutationData } from '../apollo/graphql/partner.mutation';
import type {
  BundledQuoteType,
  PolicyTierType,
  PolicyType,
  QuoteFailReasons,
  UserStageType,
} from '../apollo/graphql/quote.query';
import type { UpdateUser5DiscountInput } from '../apollo/graphql/update_user.mutation';
import type { InviteV3Data } from '../utils/invite_v3';
import type { RefiQualifiedQueryData } from '../apollo/graphql/refi_qualified.query';
import type { AutoInsuredLength } from '../types/auto_insured_length.enum';
import type { Education } from '../types/education.enum';
import type { RepairJob } from '../apollo/graphql/repair_jobs.query';

import type { SignupParams } from './signup_params';
import type { Address } from './address';
import type { SearchPolicyCar, SearchPolicyData, SearchPolicyDriver } from './policy';
import type { Car } from './car';
import type { Driver } from './driver';
import type { IntroDriveCountAnswer, IntroGoodDriverAnswer } from './intro';
import type { ReferralSource } from './referral_source.enum';
import type { NameVariation } from './name';
import type { ViolationType } from './violation';
import type { HomeOwnership } from './homeownership.enum';
import type { MaintenanceReminderOptionsType } from './maintenance_reminder';

export enum AppStepSection {
  CreateAccount,
  Challenge,
  Onboarding,
  Finished,
  Quotes,
}

export interface ShopForType {
  home: boolean;
  renters: boolean;
  umbrella: boolean;
  condo: boolean;
}

export enum DeviceTypeType {
  Android = 'android',
  Ios = 'ios',
  Web = 'web',
}

export enum StepCategory {
  Intro = 'INTRO',
  Cars = 'CARS',
  Reminder = 'SERVICES REMINDERS',
  Loan = 'LOAN',
  Account = 'ACCOUNT',
  Verify = 'VERIFY',
  PersonalInfo = 'PERSONAL INFO',
  // Adding a space at the end so ChallengePersonalInfo is different from PersonalInfo. Ideally we should separate the enum and the displayed title.
  ChallengePersonalInfo = 'PERSONAL INFO ',
  Insurance = 'INSURANCE',
  Other = 'OTHERS',
  Drivers = 'DRIVERS',
  AdditionalInfo = 'ADDITIONAL INFO', // NOTE: prefillQuotingVariation, refactor or remove after prefill quoting test done

  // Garage only flow steps, stared with GarageOnly prefix
  GarageOnlyLocation = 'GARAGE ONLY LOCATION',
  GarageOnlyCars = 'GARAGE ONLY CAR',
  GarageOnlyServices = 'GARAGE ONLY SERVICES',
  GarageOnlyService = 'GARAGE ONLY SERVICE',
  GarageOnlyDriving = 'GARAGE ONLY DRIVING',
  GarageOnlyAccount = 'GARAGE ONLY ACCOUNT',
}

export type DeviceInfoType = {
  deviceType?: DeviceTypeType;
  firebaseInstanceID?: string;
  advertisingID?: string;
  adjustDeviceID?: string;
};

interface PartnerSkippableData {
  isAutoInsured?: boolean;
  autoInsuredLength?: AutoInsuredLength;
  claims?: boolean;
  violations?: boolean;
  dateOfBirthMasked?: string;
  address?: Address;
  cars?: Car[];
  hasOccupationPrimaryDriverID?: string;
  hasOccupationSpouseDriverID?: string;
  multiplecarsdrivers?: boolean;
  homeOwnership?: HomeOwnership;
  education?: Education;
  email?: string;
}

export interface SelectedServiceType {
  insurance?: boolean | undefined;
  garage?: boolean | undefined;
  dwep?: boolean | undefined;
  refi?: boolean | undefined;
}

export interface MaintenanceReminderDataType {
  oilChangeLastCompletedTime?: MaintenanceReminderOptionsType;
  oilType?: MaintenanceReminderOptionsType;
  tireRotatedLastCompletedTime?: MaintenanceReminderOptionsType;
  brakePadsLastCompletedTime?: MaintenanceReminderOptionsType;
  odometerValue?: number;
}

export interface AppData {
  stepSection: AppStepSection;
  featureData?: FeatureData;
  abTestData?: Record<string, number | boolean>;
  partner?: PartnerData;
  publisher?: string;
  id?: string;
  name?: string;
  phone?: string;
  phoneVerified?: boolean;
  isSigned?: boolean;
  signupParams?: SignupParams;
  /** fieldsUpatedAt of the user entity */
  userFieldsUpdatedAt?: Record<string, string>;
  isHomeowner?: boolean;
  telematics?: boolean;
  /**
   * The result of the servicesplit step. This is different from selectedService in that it won't be merged with user data.
   * This is used for steps that checking the initial selection from user, like service cross sell.
   */
  serviceSplitSelectedService?: SelectedServiceType;
  /**
   * User can select insurance/refi/garage/dwep service in servicesplit page, which shows different steps in flow.
   * This represents what service user has selected, including the result of servicesplit step and the service cross sell step.
   * To check what the user selected in servicesplit page, use serviceSplitSelectedService.
   */
  selectedService?: SelectedServiceType;
  /**
   * if selectedService is only insurance
   */
  onlyInsurance?: boolean;
  /**
   * Directly go to specified service flow if matches query string 'service=' from url.
   */
  specifiedService?: string;
  /**
   * Channel of the user
   */
  channel?: string;
  /**
   * UTM source of the user
   */
  utmSource?: string;
  /**
   * UTM source from query string of signup. It's different from utmSource, which could be also from other places like firstURL.
   */
  utmSourceFromSignupQuery?: string;
  /**
   * If maintenance is accepted by user.
   *
   * See also:
   * - currentAddReminderCar
   * - maintenanceReminderData
   * - MaintenanceReminderSettledCars
   */
  maintenanceAccepted?: boolean;

  /**
   * If insurance service is accepted by user.
   */
  insuranceAccepted?: boolean;
  /**
   * If recall alerts is accepted by user.
   *
   */
  recallAlertsAccepted?: boolean;
  /**
   * Indicates which car is in the maintenance reminder setting flow
   */
  currentAddReminderCar?: SearchPolicyCar;
  /**
   * Stores all the maintenance reminder data with carId
   */
  maintenanceReminderData?: Record<string, MaintenanceReminderDataType>;
  /**
   * Stores the mapping of carId.
   */
  MaintenanceReminderSettledCars?: Set<string>;
  /**
   * Onboard returns a list of dob for the user to select. This field is the index of the user selected DOB in the list.
   */
  selectedDateOfBirthIndex?: number;
  /**
   * User entered DOB in DOB step.
   */
  manuallyEnteredDateOfBirth?: string;
  /**
   * User is insured for multiple drivers and cars
   */
  multipleCarsAndDrivers?: boolean;
  dateOfBirthMasked?: string;
  age?: number;
  address?: Address;
  driverID?: string;
  cars?: Car[];
  /** The result of cars edited by the cars step. */
  tempCars?: SearchPolicyCar[];
  drivers?: SearchPolicyDriver[];
  driverViolations?: { [key: Driver['id']]: ViolationType[] };
  discounts?: UpdateUser5DiscountInput;
  syncedDriverViolations?: boolean;
  tempDrivers?: SearchPolicyDriver[];
  searchPolicyData?: SearchPolicyData;
  mlCoverageEnabled?: boolean;
  syncedPolicyData?: boolean;
  syncedPolicyDrivers?: boolean;
  referralSource?: ReferralSource;
  mobileAppView?: boolean;
  deviceInfo?: DeviceInfoType;
  isMobileAgent?: boolean;
  mobileOS?: DeviceTypeType;
  utoken?: string;
  ptoken?: string;
  partnerSkippableData?: PartnerSkippableData;
  nameVariation?: NameVariation;
  nameVariationManualEntry?: boolean;
  primaryDriverOccupation?: boolean;
  spouseDriverOccupation?: boolean;
  spouseDriverEducation?: boolean;
  hasCarLoan?: boolean;
  hasMinNumberCarLoanPayments?: boolean | null;
  carLoanBalance?: number;
  carLoanMonthlyPayment?: number;
  income?: Income;
  // Initial car related data and flags
  initialCar?: Car;
  malCarUpdate?: InitialCar;
  addedInitialCarMileage?: boolean;
  carModelAutoSubmit?: boolean;
  carSubmodelAutoSubmit?: boolean;
  isOnboardingLite?: boolean;
  isBeyondDrivingAge?: boolean;
  hasUnsyncedPolicyChanges?: boolean;
  growthBookFeatureData?: Record<string, FeatureDefinition>;
  sessionId?: string;
  isSeoFunnel?: boolean;
  internalSource?: InternalSource;
  homeOwnershipAnswers?: HomeOwnership;
  selectedCars?: SearchPolicyCar[];
  selectedCar?: SearchPolicyCar;
  // Indicates if the user was imported from MAL
  isFromMAL?: boolean;

  // signupUser success
  signupUserSuccess?: boolean;
  // Lowest car policy prices
  lowestPrices?: LowestPrice[] | null;
  // If show service split screen
  showServiceSplit?: boolean;

  shouldAskMaritalStatus?: boolean;

  shouldSelectPartner?: boolean;

  // data for intro steps
  introDriveCount?: IntroDriveCountAnswer;
  introGoodDriver?: IntroGoodDriverAnswer;

  carPremium?: {
    premium?: number;
    term: number;
  };

  homePremium?: {
    premium?: number;
    term: number;
  };

  enableLDU?: boolean;
  /**
   * The progress of the challenge flow. It's stored in JWT and returned in Viewer.
   *
   * Since this is what's returned by Viewer, it doesn't necessarily represent the exact step the user is viewing. For
   * example, if the user is on the /challenge page and proceeds to /challengedob, there's no server interaction to update
   * this field, so the user could be on the /challengedob page while `signupChallengeStep` is still `Name`.
   */
  signupChallengeStep?: SignupChallengeStep | null;
  inviteV3DataInCookies?: InviteV3Data;
  inviteV3DataInParams?: InviteV3Data;
  /**
   * Flag for show describe another address screens
   */
  needAnotherAddress?: boolean;
  /**
   * Refi qualified data used to check eligibility for refi
   */
  refiQualifiedData?: RefiQualifiedQueryData;
  /**
   * User email
   */
  email?: string | null;
  /**
   * The selected value for step coverage
   */
  coverageSelection?: number;

  /**
   * Changes in the challenge section.
   */
  challengeSectionChanges?: {
    drivers?: boolean;
    cars?: boolean;
  };

  /**
   * Whether the user chose to add the initial car by VIN. In such case we should skip all the add initial car by YMM steps.
   */
  initialCarAddCarByVin?: boolean;

  /**
   * The input state of the addcarvin page. This is used to set default values when navigating back to the step or determine if user has
   * entered any data in the step.
   */
  addCarByVinState?: {
    plateState?: string;
    plateNumber?: string;
    vin?: string;
  };

  /**
   * TODO: Review this once signup_rm_adding_modal test is finished.
   *
   * The existence of this field indicates that it's in the `/addcarxxx` page and instead of adding an initial car, it
   * comes from /cars and is adding a new car.
   *
   * `/cars` should consume this field and remove it from appData.
   */
  addingNewCar?: {
    /** The car being added */
    car: Car;
    /**
     * Whether `car` is ready to be added.
     *
     * We use this instead of checking if `car` has all necessary fields (vin/partialVin) because we want to make sure
     * that the user confirms to add this car. If the user comes back from the mileage page all they way to the /cars
     * page, the car will still be added, which is not what the user wants.
     */
    done?: boolean;
  };

  /**
   * TODO: Review this once signup_rm_adding_modal test is finished.
   *
   * The existence of this field indicates that it's adding a driver.
   *
   * `/drivers` should consume this field and remove it from appData.
   */
  addingNewDriver?: {
    variant?: 'addSpouse' | 'addDomesticPartner';
    /** The driver being added. */
    driver?: Driver;
    /**
     * Whether `driver` is ready to be added.
     *
     * We use this instead of checking if `driver` exists because we want to make sure that the user confirms to add
     * this driver. If the user comes back from the /adddriverselectname page all they way to the /drivers page, the
     * driver will still be added, which is not what the user wants.
     */
    done?: boolean;
  };

  /**
   * State of the update DOB challenge flow.
   * The existence of this field indicates that the user wants to update their DOB in the challenge flow.
   */
  updateDobChallenge?: {
    /** The new DOB that the user wants to set */
    newDob: string;
    challenges: {
      carrier: { options: string[]; selected?: string };
      car: { options: string[]; selected?: string };
    };
    /** 0 means the user has failed the challenge. */
    remainingAttempts: number;
    success?: boolean;
  };

  clientIP?: string;

  // NOTE: pToken only used for track events
  ptokenForTrack?: string;
  // NOTE: mark user has passed the interstitial page, this front-end variable is only used for control screen display
  hasPassedInterstitial?: boolean;
  // NOTE: mark user has passed the confirm info page, this front-end variable is only used for control screen display
  hasPassedConfirmInfo?: boolean;
  // Partner data (partial)
  partnerPrefillData?: SetPartnerAPIUserMutationData['data']['setPartnerAPIUser']['icvQuestions'];

  /** Data for confirm ownership steps */
  confirmOwnership?: {
    shouldSelectFinancedCars?: boolean;
  };

  averageCarPolicyPriceByState?: number | null;

  // User signed up from garage new flow
  flowType?: FlowType;
  zip?: string;
  // Garage only flow, front end page temporary data, not stored in the backend, frontend use only.
  garageOnlyFlowPageTempData?: Record<string, unknown>;

  // Garage only flow
  repairJob?: RepairJob;
  repairJobDetail?: {
    choiceIDs?: number[];
    qnas?: { key: string; a: string; index: number }[];
    qnaOptionalJobs?: string[];
  };

  // Garage only flow, user has accepted the monthly car value update
  carValueMonthlyUpdate?: boolean;
  // Garage only flow, recall
  checkRecallAfterSignup?: boolean;
  futureRecallNotification?: boolean;

  currentOccupationDriver?: SearchPolicyDriver | null;
  currentOccupationListDriver?: SearchPolicyDriver | null;
  currentEducationDriver?: SearchPolicyDriver | null;

  /**
   * Car value for each car id
   */
  carValues?: Record<string, number>;
}

export interface QuoteAppData extends AppData {
  stage: UserStageType;
  currentBatchFinishedTiers?: PolicyTierType[];
  currentBatchCheckedCarriersCount?: number;
  currentBatchFailedReasons?: QuoteFailReasons[];
  currentQuotingProgress?: number | null;
  isQuotingInProgress?: boolean | null;
  bundledQuotes: BundledQuoteType[];
  activePolicies?: PolicyType[];
}

/**
 * Type guards for QuoteAppData
 */
export function isQuoteAppData(data: AppData): data is QuoteAppData {
  return (data as QuoteAppData).stage !== undefined;
}
